// https://blog.logrocket.com/create-drag-and-drop-component-react-dropzone/
// https://stackoverflow.com/questions/15410265/file-upload-progress-bar-with-jquery

import { useEffect, useState, useMemo, useRef } from 'react'
import { useForm } from "react-hook-form"
import { useDropzone } from 'react-dropzone'
import ReactPlayer from 'react-player'
import { Editor } from '@tinymce/tinymce-react';
import Attachable from './attachable'

const LessonForm = ({course, lesson, savedLessonAlert, cancel, updateLesson, loadChapters}) => {
  const maxFileSize = 630000000
  const uuid = useMemo(() => Date.now(), [])

  const [files, setFiles] = useState([]);
  const [mp3Url, setMp3Url] = useState(lesson?.mp3?.url);
  const [videoUrl, setVideoUrl] = useState(lesson?.video?.url || lesson?.video_url || lesson.external_video);
  const { register, handleSubmit, setFocus, watch, setValue, formState: { errors } } = useForm({defaultValues: lesson})
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps
  } = useDropzone({
    maxFiles: 1,
    accept: {
      'video/mp4': []
    },
    validator: sizeValidator,
    onDrop: acceptedFiles => {
      setFiles(acceptedFiles);
    }
  });

  const editorRef = useRef()

  const deleteVideo = () => {
    if (confirm(I18n.t('really_delete_video'))) {
      jQuery.ajax({
        url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/delete_video`,
        method: 'DELETE',
        success: () => {
          setFiles([])
          setVideoUrl(null)
          setValue('external_video', null)
          loadChapters()
        }
      })
    }
  }

  const deleteMp3 = () => {
    if (confirm(I18n.t('really_delete_mp3'))) {
      jQuery.ajax({
        url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/delete_mp3`,
        method: 'DELETE',
        success: () => {
          setMp3Url(null)
          setValue('mp3', null)
          loadChapters()
        }
      })
    }
  }

  const uploadMp3 = (e) => {
    if (!e.target.files[0]) return

    const formData = new FormData()
    formData.append('mp3', e.target.files[0])
    formData.append("Content-Type", e.target.files[0].type);

    jQuery.ajax({
      url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/upload_mp3`,
      dataType: "xml",
      cache: false,
      method: 'POST',
      processData: false,
      contentType: false,
      data: formData,
      success: () => {
        jQuery.ajax({
          url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}`,
          success: (response) => {
            setMp3Url(response.lesson.audio_url)
          }
        })
      },
      error: (data) => {
        console.log(data)
      }
    })
  }

  useEffect(() => {
    if (files.length) {
      const file = files[0]
      const formData = new FormData()
      formData.append('video', file)
      formData.append("Content-Type", file.type);

      jQuery.ajax({
        url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/upload_video`,
        dataType: "xml",
        cache: false,
        method: 'POST',
        processData: false,
        contentType: false,
        data: formData,
        xhr: () => {
          var xhr = new window.XMLHttpRequest();
          xhr.upload.addEventListener("progress", function(evt) {
              if (evt.lengthComputable) {
                  var percentComplete = ((evt.loaded / evt.total) * 100);
                  $(".progress-bar").width(percentComplete + '%');
              }
          }, false);
          return xhr;
        },
        success: () => {
          jQuery.ajax({
            url: `/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}`,
            success: (response) => {
              setVideoUrl(response.lesson.video_url)
              loadChapters()
            }
          })
        },
        error: (data) => {
          console.log(data)
        }
      })
    }
  }, [files])

  const watched = watch()

  function sizeValidator(file) {
    if (file.size > maxFileSize) {
      return {
        code: "file-too-large",
        message: `File is larger ${maxFileSize}`
      };
    }

    return null
  }

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);

  useEffect(() => {
    setFocus("name");
  }, [setFocus]);

  const changeDesc = (content) => {
    setValue('desc', content)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="mb-0"><strong>{I18n.t('lesson')}</strong> {'-'} {watched?.name}</h4>
      </div>
      <div className="card-body">

        <form onSubmit={handleSubmit(updateLesson)}>
          <div className={`form-group ${errors.name && 'field_with_errors'}`}>
            <label htmlFor="name">{I18n.t('activerecord.attributes.lesson.name')}</label>
            <input {...register('name', {required: true})} id="name" type="text" className="form-control" />
            {errors.name?.type === "required" && <label className="label label-danger">{I18n.t('simple_form.required.text')}</label>}
          </div>

          {videoUrl ?
            <div className="mb-2">
              <label className="radio_buttons">{I18n.t('activerecord.attributes.lesson.video')}</label>
              <ReactPlayer url={videoUrl + "?time=" + uuid} controls width={'100%'} />

              <div className="mt-2">
                <a href="#" className="btn btn-xs btn-danger" onClick={(e) => !e.preventDefault() && deleteVideo()}>{I18n.t('delete_video')}</a>
              </div>
            </div> :

            <>
              <div className="i-checks radio_buttons">
                <label className="radio_buttons">{I18n.t('activerecord.attributes.lesson.video')}</label>
                <span className="radio">
                  <input {...register('video_type')} className="radio_buttons form-control" type="radio" value="external" id="video_type1" />
                  <label className="collection_radio_buttons" htmlFor="video_type1">{I18n.t('video_types.external')}</label>
                </span>

                <span className="radio">
                  <input {...register('video_type')} className="radio_buttons form-control" type="radio" value="file" id="video_type2"/>
                  <label className="collection_radio_buttons" htmlFor="video_type2">{I18n.t('video_types.file')}</label>
                </span>
              </div>

              {watched?.video_type == 'file' ?
                <section className="mb-2">
                  <div className={`${files && files.length ? 'hide' : ''}`}>
                    <div {...getRootProps({ className: 'dropzone' })}>
                      <input {...getInputProps()} />
                      <p>{I18n.t('dragdrop')}</p>
                      <em>{I18n.t('dragdrop_help')}</em>
                    </div>
                  </div>

                  <aside>
                    {files && files.length ?
                      <>
                        {files.map(file => (
                          <div className="alert alert-info" key={file.path}>
                            <h4>{I18n.t('video_uploading')}</h4>
                            {file.path} - {Math.round(file.size / 1000 / 1000)} MB

                            <div className="progress">
                              <div className="progress-bar" style={{width: '0%'}}></div>
                            </div>
                          </div>
                        ))}
                      </> : null
                    }

                    {fileRejections && fileRejections.length ?
                      <>
                        <h4>Rejected files</h4>
                        {fileRejections.map(({ file, errors }) => (
                          <div className="alert alert-danger" key={file.path}>
                            <span className="label label-danger">{file.path} - {Math.round(file.size / 1000 / 1000)} MB </span>
                            <ul>
                              {errors.map(e => <li key={e.code}>{e.message}</li>)}
                            </ul>
                          </div>
                          )
                        )}
                      </> : null
                    }
                  </aside>
                </section> : null
              }

              {watched?.video_type == 'external' ?
                <div className={`form-group ${errors.external_video && 'field_with_errors'}`}>
                  <label htmlFor="external_video">{I18n.t('activerecord.attributes.lesson.external_video')}</label>
                  <input {...register('external_video')} id="external_video" type="text" className="form-control" placeholder="https://" />
                </div> : null
              }
            </>
          }

          <div>
            <div className={`form-group`}>
              <label htmlFor="mp3">{I18n.t('activerecord.attributes.lesson.mp3')}</label>
              {mp3Url ?
                <div>
                  <audio src={mp3Url} controls />
                  <br/>
                  <small><a href="#" className="text-danger" onClick={deleteMp3}>{I18n.t('delete')}</a></small>
                </div> :
                <>
                  <input onChange={uploadMp3} id="cert" type="file" className="form-control" />
                  <small>*.mp3</small>
                </>
              }
            </div>
          </div>

          <label>{I18n.t('activerecord.attributes.lesson.desc')}</label>
          <Editor
            key={uuid}
            tinymceScriptSrc={'https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.0.1/tinymce.min.js'}
            onInit={(evt, editor) => editorRef.current = editor}
            initialValue={lesson.desc}
            onChange={(x) => changeDesc(x.level.content)}
            init={{
              height: 300,
              language: I18n.locale,
              language_url: `https://dogres.cz/tiny_langs/${I18n.locale}.js`,
              menubar: false,
              statusbar: false,
              selector: 'textarea#context-menu',
              contextmenu: 'copy paste link image',
              setup: function (editor) {
                const openGallery = () => {
                  $.fancybox.open({
                    src: '/trainer/photos',
                    type: 'iframe'
                  })
                };
                editor.ui.registry.addButton("gallery", {
                  icon: 'image',
                  onAction: openGallery
                });
              },
              link_default_target: '_blank',
              relative_urls: false,
              convert_urls : false,
              link_assume_external_targets: true,
              plugins: [
                'advlist', 'autoresize', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'table', 'code', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | ' +
                'link gallery bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'table code paste | removeformat',
              content_style: 'body { font-family:sans-serif; font-size:14px }'
            }}
          />

          <Attachable
            saveUrl={`/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/upload_attachment`}
            attachmentsUrl={`/api/courses/${course.id}/chapters/${lesson.chapter_id}/lessons/${lesson.id}/attachments`}
          />

          {savedLessonAlert ?
            <div className="alert alert-success mt-2">
              <i className="fa fa-check-circle mr-2"></i>
              {I18n.t('saved')}
            </div> : null
          }

          <div className="mt-3">
            <button type="submit" className="btn btn-themecolor">{I18n.t('save')}</button>
            <button className="btn btn-secondary ml-2" onClick={cancel}>{I18n.t('cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LessonForm